import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import CompanyLogo from '../components/CompanyLogo';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';
import { LogIn, Mail, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Login = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        // Store auth info in localStorage
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        toast.success(language === 'es' ? '¡Bienvenido!' : 'Welcome!');
        navigate('/admin');
      } else {
        setError(data.detail || (language === 'es' ? 'Credenciales inválidas' : 'Invalid credentials'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageToggle />
      </div>
      
      <Card className="w-full max-w-md bg-white border-gray-200 shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <Link to="/" className="hover:opacity-80 transition-opacity cursor-pointer">
              <CompanyLogo className="h-16 w-auto" />
            </Link>
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {language === 'es' ? 'Iniciar Sesión' : 'Admin Login'}
          </CardTitle>
          <p className="text-gray-500 mt-2">
            {language === 'es' 
              ? 'Ingrese sus credenciales para acceder'
              : 'Enter your credentials to access the dashboard'}
          </p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-700">
                {language === 'es' ? 'Usuario' : 'Username'}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admincrm"
                  className="pl-10 h-11 border-gray-300"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password" className="text-gray-700">
                {language === 'es' ? 'Contraseña' : 'Password'}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-11 border-gray-300"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button 
              type="submit"
              disabled={loading}
              style={{ backgroundColor: COMPANY.primaryColor }}
              className="w-full h-12 text-base text-white hover:opacity-90"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  {language === 'es' ? 'Ingresando...' : 'Signing in...'}
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <LogIn className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Iniciar Sesión' : 'Sign In'}
                </span>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-gray-600 font-medium mb-3">
                {language === 'es' ? 'Acceso Admin Incluye' : 'Admin Access Includes'}
              </p>
              <ul className="text-sm text-gray-500 space-y-2">
                <li>✓ {language === 'es' ? 'Gestión de Leads' : 'Lead Management'}</li>
                <li>✓ {language === 'es' ? 'CRM de Clientes' : 'Client CRM'}</li>
                <li>✓ {language === 'es' ? 'Presupuestos y PDF' : 'Estimates & PDF'}</li>
                <li>✓ {language === 'es' ? 'Configuración de Empresa' : 'Company Settings'}</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t text-center">
            <p className="text-sm text-gray-500 mb-2">
              {language === 'es' ? '¿Es propietario de vivienda?' : 'Are you a homeowner?'}
            </p>
            <Link 
              to="/homeowner/login" 
              className="text-sm font-medium hover:underline"
              style={{ color: COMPANY.primaryColor }}
            >
              {language === 'es' ? 'Acceder al Portal de Propietarios' : 'Access Homeowner Portal'}
            </Link>
          </div>

          <div className="mt-4 text-center">
            <a 
              href="https://jymegafcconstruction.net/app.crm" 
              className="text-sm text-gray-400 hover:text-gray-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {language === 'es' ? 'Ir al sitio principal' : 'Go to main site'}
            </a>
          </div>
        </CardContent>
      </Card>
      
      <div className="absolute bottom-4 text-center text-gray-400 text-sm">
        <p>ProEstimate v2.0 • Powered by Emergent Labs</p>
      </div>
    </div>
  );
};

export default Login;
